import React, { useState, useEffect, useRef } from 'react'
import {
  Box,
  Chip,
  Divider,
  Drawer,
  Grid,
  IconButton,
  Link,
  Stack,
  Typography,
  Button,
  CircularProgress,
  TextField,
  InputAdornment,
  Alert,
} from '@mui/material'
import StorefrontIcon from '@mui/icons-material/Storefront'
import CloseIcon from '@mui/icons-material/Close'
import RefreshIcon from '@mui/icons-material/Refresh'
import BusinessIcon from '@mui/icons-material/Business'
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail'
import EmailIcon from '@mui/icons-material/Email'
import PersonIcon from '@mui/icons-material/Person'
import PhoneIcon from '@mui/icons-material/Phone'
import PublicIcon from '@mui/icons-material/Public'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import { SHOPIFY_ADMIN_BASE_URL } from '../../constants/baseURLs'

import {
  SMBData,
  SMBDetailData,
  WebsiteScrapeMetadata,
  ContentBlockData,
  ScrapedItemData,
  shopifyApi,
} from '../../services/api'
import { paymentsApi } from '../../services/paymentsApi'
import { usePaymentContext } from '../../contexts/PaymentContext'
import { useNotification } from '../../contexts/NotificationContext'
import { BusinessDocumentUpload } from './BusinessDocumentUpload'
// import { CreditWidget } from './CreditWidget'
import { TotalAiReplyWidget } from './TotalAiReplyWidget'
import { ShopifyConfig } from './ShopifyConfig'

interface SMBDetailsDrawerProps {
  open: boolean
  smb: SMBDetailData | null
  onClose: () => void
  onRefreshWebsite?: (smb: SMBDetailData | SMBData) => void
  refreshing?: boolean
  scrapeMeta?: WebsiteScrapeMetadata | null
  loading?: boolean
  onReload?: (smbId: string) => void | Promise<void>
}

const formatDateTime = (value?: string | null) => {
  if (!value) return 'Not yet recorded'
  try {
    return new Date(value).toLocaleString()
  } catch {
    return value
  }
}

export const SMBDetailsDrawer: React.FC<SMBDetailsDrawerProps> = ({
  open,
  smb,
  onClose,
  onRefreshWebsite,
  refreshing = false,
  scrapeMeta,
  loading = false,
  onReload,
}) => {
  const { smb: contextSmb, hasAnySubscription } = usePaymentContext()
  const [isSubActive, setIsSubActive] = useState<boolean>(false)
  const [shopifyDomain, setShopifyDomain] = useState('')
  const [shopifyConnecting, setShopifyConnecting] = useState(false)
  const [shopifyError, setShopifyError] = useState('')
  const [shopifySyncStatus, setShopifySyncStatus] = useState<string>('loading')
  const [syncedStoreDomain, setSyncedStoreDomain] = useState<string | null>(null)
  
  const { showNotification } = useNotification()

  useEffect(() => {
    if (!smb?.id) return
    if (contextSmb?.id === smb.id) {
      setIsSubActive(hasAnySubscription)
    } else {
      paymentsApi.getBillingSummary(smb.id)
        .then((res) => setIsSubActive(res.has_active_subscription))
        .catch(() => setIsSubActive(false))
    }
  }, [smb?.id, contextSmb?.id, hasAnySubscription])
  const prevStatus = useRef<string>('none')

  // Poll sync status
  useEffect(() => {
    let intervalId: NodeJS.Timeout

    const checkProductsSyncStatus = async () => {
      if (!smb?.id || !open) return
      try {
        const res = await shopifyApi.getProductsSyncStatus(smb.id)
        const currentStatus = res.status
        const domain = res.store_domain || 'Shopify store'

        const isPendingInSession = sessionStorage.getItem(`shopify_sync_pending_${domain}`) === 'true'

        // Check for state transitions to show toasts
        const didTransitionToComplete = 
          (currentStatus === 'completed' && ['pending', 'in_progress'].includes(prevStatus.current)) ||
          (currentStatus === 'completed' && prevStatus.current === 'none' && isPendingInSession)

        const didTransitionToFailed = 
          (currentStatus === 'failed' && ['pending', 'in_progress'].includes(prevStatus.current)) ||
          (currentStatus === 'failed' && prevStatus.current === 'none' && isPendingInSession)

        if (didTransitionToComplete) {
          showNotification(`Successfully fetched all products from ${domain}!`, 'success')
          sessionStorage.removeItem(`shopify_sync_pending_${domain}`)
        } else if (didTransitionToFailed) {
          showNotification(`Failed to fetch products from ${domain}.`, 'error')
          sessionStorage.removeItem(`shopify_sync_pending_${domain}`)
        }

        // If it's midway through syncing and we hadn't marked it, mark it now so it fires when done
        if (['pending', 'in_progress'].includes(currentStatus)) {
          sessionStorage.setItem(`shopify_sync_pending_${domain}`, 'true')
        }

        prevStatus.current = currentStatus

        setShopifySyncStatus(currentStatus)
        setSyncedStoreDomain((prev) => (prev === res.store_domain ? prev : res.store_domain))
        
        // Stop polling if complete, failed, or none
        if (['completed', 'failed', 'none'].includes(currentStatus)) {
          if (intervalId) clearInterval(intervalId)
        }
      } catch (err) {
        console.error('Failed to get Shopify sync status', err)
      }
    }

    if (open && smb?.id) {
      checkProductsSyncStatus() // check immediately
      intervalId = setInterval(checkProductsSyncStatus, 5000)
    }

    return () => {
      if (intervalId) clearInterval(intervalId)
    }
  }, [open, smb?.id])

  const handleRefresh = () => {
    if (smb && onRefreshWebsite) {
      onRefreshWebsite(smb)
    }
  }

  const handleShopifyConnect = async () => {
    const domain = shopifyDomain.trim()
    if (!domain || !smb?.id) return

    const fullDomain = domain.includes('.myshopify.com') ? domain : `${domain}.myshopify.com`

    setShopifyError('')
    setShopifyConnecting(true)
    try {
      const { authorize_url } = await shopifyApi.install(smb.id, fullDomain)
      window.location.href = authorize_url
    } catch (err: any) {
      setShopifyError(err.response?.data?.detail || 'Please enter a valid store domain')
      setShopifyConnecting(false)
    }
  }

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', sm: 420 }, p: { xs: 2, sm: 3 }, borderRadius: { xs: 0, sm: '16px 0 0 16px' } } }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <BusinessIcon color="primary" />
          <Typography variant="h6">Business Overview</Typography>
        </Stack>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>

      <Divider sx={{ my: 2 }} />

      {loading ? (
        <Stack alignItems="center" justifyContent="center" sx={{ py: 6 }}>
          <CircularProgress size={28} />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Loading business details…
          </Typography>
        </Stack>
      ) : smb ? (
        <Stack spacing={3} sx={{ 
          overflowY: 'auto', 
          overflowX: 'hidden', 
          pb: 4, 
          pr: 1.5, // Gap between content and scrollbar
          scrollbarWidth: 'thin', 
          scrollbarColor: 'rgba(0,0,0,0.15) transparent',
          '&::-webkit-scrollbar': { width: '3px' },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.15)', borderRadius: '3px' },
          '&::-webkit-scrollbar-thumb:hover': { background: 'rgba(0,0,0,0.25)' }
        }}>
          {(() => {
            const sectionsByType = (smb.website_sections ?? []).reduce(
              (acc, section) => {
                const key = section.block_type || 'website_section'
                if (!acc[key]) {
                  acc[key] = []
                }
                acc[key].push(section)
                return acc
              },
              {} as Record<string, ContentBlockData[]>
            )

            const hoursLines = smb.business_hours?.length
              ? smb.business_hours
              : (sectionsByType['business_hours'] ?? []).map((section) => section.body)

            const servicesSections = sectionsByType['services'] ?? []
            const productsSections = sectionsByType['products'] ?? []
            const pricingSections = sectionsByType['pricing'] ?? []
            const aboutSections = sectionsByType['about'] ?? []
            const testimonialsSections = sectionsByType['testimonials'] ?? []
            const overviewSections = sectionsByType['website_section'] ?? []
            const genericOverview = sectionsByType['website_overview'] ?? []

            const renderItems = (items?: ScrapedItemData[] | null) => {
              if (!items?.length) return null
              return (
                <Stack spacing={1.5} sx={{ mt: 1 }}>
                  {items.map((item, idx) => (
                    <Box key={`${item.title || item.description || idx}-${idx}`} sx={{ pl: 1 }}>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: item.description ? 0.5 : 0 }}>
                        {item.title && (
                          <Typography variant="subtitle2" fontWeight="bold">
                            {item.title}
                          </Typography>
                        )}
                        <Stack direction="row" spacing={1} alignItems="center">
                          {item.price && <Chip label={item.price} size="small" color="secondary" variant="outlined" />}
                          {item.duration && <Chip label={item.duration} size="small" variant="outlined" />}
                        </Stack>
                      </Stack>
                      {item.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
                          {item.description}
                        </Typography>
                      )}
                      {item.cta && (
                        <Typography variant="caption" color="text.secondary">
                          {item.cta}
                        </Typography>
                      )}
                      {item.source_url && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                          Source:{' '}
                          <Link href={item.source_url} target="_blank" rel="noopener">
                            {item.source_url}
                          </Link>
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Stack>
              )
            }

            const renderSections = (
              sections: ContentBlockData[],
              heading: string,
            ) => {
              if (!sections?.length) return null
              return (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    {heading}
                  </Typography>
                  <Stack spacing={2}>
                    {sections.map((section, idx) => (
                      <Box key={`${heading}-${idx}`}>
                        {section.title && section.title !== heading && (
                          <Typography variant="subtitle2" fontWeight="bold">
                            {section.title}
                          </Typography>
                        )}
                        {renderItems(section.items)}
                        {(!section.items || section.items.length === 0) && section.body && (
                          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
                            {section.body}
                          </Typography>
                        )}
                        {section.source_url && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            Source:{' '}
                            <Link href={section.source_url} target="_blank" rel="noopener">
                              {section.source_url}
                            </Link>
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </Stack>
                </Box>
              )
            }

            return (
              <>
                <Box>
                  <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap">
                    <Typography variant="h5" fontWeight="bold">
                      {smb.display_name || smb.name}
                    </Typography>
                    <Chip
                      size="small"
                      label={isSubActive ? 'Active' : 'Inactive'}
                      color={isSubActive ? 'success' : 'error'}
                      variant="outlined"
                    />
                  </Stack>
                </Box>

                 {smb?.id && <Box>
                  {/* <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Available Conversations
                  </Typography> */}
                  <Stack spacing={1.5}>
                    {/* <CreditWidget smbId={smb.id} /> */}
                    <TotalAiReplyWidget smbId={smb.id} />
                  </Stack>
                </Box>}

                {smb.website && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Online Presence
                    </Typography>
                    <Stack spacing={1.5}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <PublicIcon fontSize="small" color="action" />
                        <Typography variant="body2">
                          Website:{' '}
                          <Link href={smb.website} target="_blank" rel="noopener">
                            {smb.website}
                          </Link>
                        </Typography>
                      </Stack>
                      {onRefreshWebsite && (
                        <Box sx={{ mt: 1 }}>
                          <Button
                            fullWidth
                            variant="outlined"
                            startIcon={refreshing ? undefined : <RefreshIcon fontSize="small" />}
                            onClick={handleRefresh}
                            disabled={refreshing}
                            sx={{
                              height: 40,
                              borderRadius: 1.5,
                              fontWeight: 600,
                              textTransform: 'none',
                              borderColor: 'primary.main',
                              color: 'primary.main',
                              '&:hover': {
                                borderColor: 'primary.dark',
                                bgcolor: 'primary.50'
                              }
                            }}
                          >
                            {refreshing ? <CircularProgress size={18} thickness={6} /> : 'Refresh Website Data'}
                          </Button>
                          {scrapeMeta?.last_run_at && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75, textAlign: 'center' }}>
                              Last scraped {formatDateTime(scrapeMeta.last_run_at)} ({scrapeMeta.last_status || 'unknown'})
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Stack>
                  </Box>
                )}

                {(smb.document_name || smb.document_original_path) && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Business Document
                    </Typography>
                    <BusinessDocumentUpload smb={smb} onReload={onReload} />
                  </Box>
                )}

                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Shopify Store
                  </Typography>
                  <Stack spacing={1.5}>
                    {shopifySyncStatus === 'loading' ? (
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 1 }}>
                        <CircularProgress size={16} />
                        <Typography variant="body2" color="text.secondary">Checking sync status...</Typography>
                      </Stack>
                    ) : shopifySyncStatus !== 'none' ? (
                      <Stack spacing={1.5}>
                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                            <Stack 
                              direction="row" 
                              spacing={1} 
                              alignItems="center" 
                              sx={{ overflow: 'hidden', mr: 1, minWidth: 0 }}
                              title={syncedStoreDomain || 'Store Connected'}
                            >
                              <StorefrontIcon fontSize="small" color="success" sx={{ flexShrink: 0 }} />
                              <Typography variant="body2" noWrap>
                                {syncedStoreDomain ? (
                                  <Link 
                                    href={`${SHOPIFY_ADMIN_BASE_URL}/${syncedStoreDomain.replace('.myshopify.com', '')}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    {syncedStoreDomain}
                                  </Link>
                                ) : (
                                  'Store Connected'
                                )}
                              </Typography>
                            </Stack>
                          <Chip size="small" label="Connected" color="success" variant="outlined" sx={{ minWidth: 80, flexShrink: 0 }} />
                        </Stack>
                        
                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                          <Typography variant="caption" color="text.secondary">
                            Products Sync Status:
                          </Typography>
                          <Stack direction="row" spacing={1} alignItems="center">
                          <Chip 
                                size="small" 
                                label={shopifySyncStatus.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())} 
                                color={
                                  shopifySyncStatus === 'completed' ? 'success' : 
                                  shopifySyncStatus === 'failed' ? 'error' : 'warning'
                                } 
                                variant="outlined" 
                                sx={{ minWidth: 80 }}
                              />
                            {shopifySyncStatus === 'in_progress' && (
                              <CircularProgress size={12} thickness={4} />
                            )}
                          </Stack>
                        </Stack>
                        <ShopifyConfig smbId={smb.id || ""} />
                      </Stack>
                    ) : (
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="stretch">
                        <TextField
                          placeholder="your-store.myshopify.com"
                          value={shopifyDomain}
                          onChange={(e) => setShopifyDomain(e.target.value)}
                          disabled={shopifyConnecting}
                          size="small"
                          sx={{ flex: 1 }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <StorefrontIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                              </InputAdornment>
                            ),
                            sx: { height: 40, borderRadius: 1.5 }
                          }}
                        />
                        <Button
                          variant="contained"
                          onClick={handleShopifyConnect}
                          disabled={shopifyConnecting || !shopifyDomain.trim()}
                          sx={{
                            height: 40,
                            px: 2.5,
                            borderRadius: 1.5,
                            fontWeight: 600,
                            whiteSpace: 'nowrap',
                            minWidth: { sm: 100 }
                          }}
                        >
                          {shopifyConnecting ? <CircularProgress size={18} thickness={6} /> : 'Connect'}
                        </Button>
                      </Stack>
                    )}
                    {shopifySyncStatus === 'failed' && (
                       <Alert severity="error" sx={{ py: 0 }}>The product sync failed, but your store remains connected. Manual retry will be available soon.</Alert>
                    )}
                    {shopifyError && <Alert severity="error" sx={{ py: 0 }}>{shopifyError}</Alert>}
                  </Stack>
                </Box>

                {(smb.owner_name || smb.owner_email || smb.phone || smb.whatsapp_phone || smb.email) && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Key Contacts
                    </Typography>
                    <Stack spacing={1.5}>
                      {smb.owner_name && (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <PersonIcon fontSize="small" color="action" />
                          <Typography variant="body2">
                            Owner: {smb.owner_name}
                          </Typography>
                        </Stack>
                      )}
                      {smb.owner_email && (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <EmailIcon fontSize="small" color="action" />
                          <Typography variant="body2">
                            Owner Email: {smb.owner_email}
                          </Typography>
                        </Stack>
                      )}
                      {smb.phone && (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <PhoneIcon fontSize="small" color="action" />
                          <Typography variant="body2">
                            Business Phone: {smb.phone.startsWith('+') ? smb.phone : `+${smb.phone}`}
                          </Typography>
                        </Stack>
                      )}
                      {smb.whatsapp_phone && (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <WhatsAppIcon fontSize="small" color="action" />
                          <Typography variant="body2">
                            WhatsApp: {smb.whatsapp_phone.startsWith('+') ? smb.whatsapp_phone : `+${smb.whatsapp_phone}`}
                          </Typography>
                        </Stack>
                      )}
                      {smb.email && (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <AlternateEmailIcon fontSize="small" color="action" />
                          <Typography variant="body2">General Email: {smb.email}</Typography>
                        </Stack>
                      )}
                    </Stack>
                  </Box>
                )}

                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Additional Details
                  </Typography>
                  <Grid container spacing={1}>
                    <Grid item xs={12}>
                      <Typography variant="body2">
                        Created: {formatDateTime(smb.created_at)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2">
                        Last Updated: {formatDateTime(smb.updated_at)}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>

                {hoursLines.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Business Hours
                    </Typography>
                    <Stack spacing={1}>
                      {hoursLines.map((line, idx) => (
                        <Typography
                          key={`hours-${idx}`}
                          variant="body2"
                          color="text.secondary"
                          sx={{ whiteSpace: 'pre-line' }}
                        >
                          {line}
                        </Typography>
                      ))}
                    </Stack>
                  </Box>
                )}

                {renderSections(servicesSections, 'Services')}
                {renderSections(productsSections, 'Products')}
                {renderSections(pricingSections, 'Pricing')}
                {renderSections(aboutSections, 'About')}
                {renderSections(testimonialsSections, 'Testimonials')}
                {renderSections(genericOverview.length ? genericOverview : overviewSections, 'Website Overview')}

                {smb.faqs && smb.faqs.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Website FAQs
                    </Typography>
                    <Stack spacing={2}>
                      {smb.faqs.map((faq, idx) => (
                        <Box key={`${idx}-${faq.question}`}>
                          <Typography variant="subtitle2" fontWeight="bold">
                            {faq.question}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
                            {faq.answer}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                )}

                {smb.social_links && smb.social_links.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Social Profiles
                    </Typography>
                    <Stack spacing={1}>
                      {smb.social_links.map((link) => (
                        <Typography key={`${link.platform}-${link.url}`} variant="body2">
                          {link.platform.charAt(0).toUpperCase() + link.platform.slice(1)}:{' '}
                          <Link href={link.url} target="_blank" rel="noopener">
                            {link.url}
                          </Link>
                          {link.source === 'website_scrape' && (
                            <Chip label="scraped" size="small" color="info" variant="outlined" sx={{ ml: 1 }} />
                          )}
                        </Typography>
                      ))}
                    </Stack>
                  </Box>
                )}
              </>
            )
          })()}
        </Stack>
      ) : (
        <Typography variant="body2" color="text.secondary">
          Select a business to view its details.
        </Typography>
      )}
    </Drawer>
  )
}

export default SMBDetailsDrawer
